import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    Smartphone,
    Code2,
    Zap,
    Layers,
    CheckCircle,
    Monitor,
    Tablet,
    Watch,
    ArrowRight,
    SmartphoneCharging
} from "lucide-react";

export default function Flutter() {
    const navigate = useNavigate();
    const features = [
        {
            icon: <Smartphone className="w-6 h-6" />,
            title: "Multi-Plateformes",
            description: "Une seule codebase pour iOS, Android, Web, Windows, macOS et Linux"
        },
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Hot Reload",
            description: "Visualisez instantanément les changements sans perdre l'état de l'application"
        },
        {
            icon: <Layers className="w-6 h-6" />,
            title: "Widgets Personnalisables",
            description: "Bibliothèque riche de widgets Material et Cupertino entièrement personnalisables"
        },
        {
            icon: <Monitor className="w-6 h-6" />,
            title: "Performances Natives",
            description: "Compilation en code natif pour des performances exceptionnelles"
        }
    ];

    const useCases = [
        "Applications mobiles cross-platform avec UI complexe",
        "Startups nécessitant un MVP rapide iOS et Android",
        "Applications e-commerce avec expérience fluide",
        "Applications de productivité et collaboration",
        "Jeux 2D casual et applications graphiques"
    ];

    const whyWeUse = [
        {
            title: "Développement Rapide",
            description: "Un seul code pour toutes les plateformes = économie de temps et de budget"
        },
        {
            title: "UI Magnifique",
            description: "Créez des interfaces utilisateur pixel-perfect avec des animations fluides"
        },
        {
            title: "Soutenu par Google",
            description: "Framework stable avec roadmap claire et support à long terme"
        }
    ];

    return (
        <div className="space-y-24">
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900/30 to-sky-900/30 backdrop-blur-xl border border-blue-400/30 p-12 md:p-16"
            >
                {/* Effet de fond animé */}
                <motion.div
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.2, 0.5, 0.2]
                    }}
                    transition={{ duration: 10, repeat: Infinity }}
                    className="absolute bottom-0 left-0 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl"
                />

                <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                            className="inline-flex items-center gap-3 px-4 py-2 bg-sky-500/20 rounded-full mb-6 border border-sky-400/30"
                        >
                            <div className="w-3 h-3 bg-sky-400 rounded-full animate-pulse" />
                            <span className="text-sky-300 font-semibold text-sm">UI Toolkit Multi-plateforme</span>
                        </motion.div>

                        <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6">
                            Flutter
                        </h2>

                        <p className="text-xl md:text-2xl text-sky-300 mb-6 font-semibold">
                            Build Beautiful Apps for Any Screen
                        </p>

                        <p className="text-gray-300 text-md md:text-lg leading-relaxed mb-8">
                            Flutter transforme le développement mobile avec son approche unique basée sur
                            les widgets. Créez des applications natives compilées magnifiques pour mobile,
                            web et desktop à partir d'une seule base de code.
                        </p>

                        <div className="flex flex-wrap gap-3">
                            {["Dart", "Widgets", "Hot Reload", "Material Design"].map((tag, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.5 + i * 0.1 }}
                                    className="px-4 py-2 bg-sky-500/10 border border-sky-400/20 rounded-full text-sky-300 text-[12px] md:text-sm font-medium"
                                >
                                    {tag}
                                </motion.span>
                            ))}
                        </div>
                    </div>

                    {/* Logo Flutter animé */}
                    <motion.div
                        className="flex items-center justify-center"
                    >
                        <div className="relative w-64 h-64">
                            {/* Formes géométriques Flutter-like */}
                            <motion.div
                                animate={{ rotate: [0, 180, 360] }}
                                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                <svg width="200" height="200" viewBox="0 0 100 100">
                                    <motion.path
                                        d="M 50 10 L 90 50 L 50 90 L 20 60 L 40 40 L 50 50 Z"
                                        fill="none"
                                        stroke="url(#flutterGradient)"
                                        strokeWidth="3"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    />
                                    <defs>
                                        <linearGradient id="flutterGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#54C5F8" />
                                            <stop offset="100%" stopColor="#02569B" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </motion.div>

                            {/* Centre avec icônes de devices */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="relative">
                                    {[Smartphone, Tablet, Monitor, Watch].map((Icon, index) => (
                                        <motion.div
                                            key={index}
                                            className="absolute"
                                            style={{
                                                top: `${40 * Math.sin((index * Math.PI) / 2)}px`,
                                                left: `${40 * Math.cos((index * Math.PI) / 2)}px`,
                                            }}
                                            animate={{
                                                y: [0, -10, 0],
                                                opacity: [0.7, 1, 0.7]
                                            }}
                                            transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                        </motion.div>
                                    ))}

                                    <motion.div
                                        className="w-16 h-16 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-2xl"
                                        animate={{
                                            scale: [1, 1.1, 1],
                                            boxShadow: [
                                                '0 0 20px rgba(56, 189, 248, 0.5)',
                                                '0 0 40px rgba(2, 132, 199, 0.7)',
                                                '0 0 20px rgba(56, 189, 248, 0.5)'
                                            ]
                                        }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    >
                                        <span className="text-white font-bold text-2xl">F</span>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Points Forts */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <h3 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-12 text-center">
                    Avantages de<span className="bg-gradient-to-r from-sky-400 via-blue-400 to-sky-600 bg-clip-text text-transparent px-2 sm:px-4">Flutter</span>
                </h3>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            whileHover={{
                                scale: 1.05,
                                y: -5,
                                transition: { type: "spring", stiffness: 400 }
                            }}
                            className="bg-gradient-to-br from-blue-900/20 to-sky-900/20 backdrop-blur-xl rounded-2xl p-6 border border-sky-500/20 hover:border-sky-400/40 transition-all duration-300 group"
                        >
                            <motion.div
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                className="w-14 h-14 bg-gradient-to-br from-sky-500/30 to-blue-500/30 rounded-xl flex items-center justify-center mb-4 group-hover:shadow-xl group-hover:shadow-sky-500/30 transition-all duration-300"
                            >
                                <div className="text-sky-400">
                                    {feature.icon}
                                </div>
                            </motion.div>

                            <h4 className="text-xl font-bold text-white mb-2">
                                {feature.title}
                            </h4>
                            <p className="text-gray-300">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Cas d'Usage */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="grid lg:grid-cols-2 gap-12"
            >
                <div>
                    <h3 className="text-3xl font-bold text-white mb-6">
                        Projets Parfaits pour Flutter
                    </h3>
                    <div className="space-y-4">
                        {useCases.map((useCase, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.6 + index * 0.1 }}
                                className="flex items-start gap-3 group"
                            >
                                <motion.div
                                    whileHover={{ scale: 1.2, rotate: 90 }}
                                    className="mt-1"
                                >
                                    <CheckCircle className="w-5 h-5 text-sky-400 flex-shrink-0" />
                                </motion.div>
                                <p className="text-gray-300 group-hover:text-white transition-colors">
                                    {useCase}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-3xl font-bold text-white mb-6">
                        Pourquoi Choisir Flutter
                    </h3>
                    <div className="space-y-6">
                        {whyWeUse.map((reason, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.7 + index * 0.1 }}
                                whileHover={{ x: 10 }}
                                className="bg-gradient-to-r from-sky-500/10 to-transparent backdrop-blur-sm rounded-xl p-6 border-l-4 border-sky-500"
                            >
                                <h4 className="text-xl font-bold text-sky-300 mb-2">
                                    {reason.title}
                                </h4>
                                <p className="text-gray-300">
                                    {reason.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Stack Complémentaire */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-gradient-to-br from-blue-900/20 to-sky-900/20 backdrop-blur-xl rounded-3xl p-12 border border-sky-500/20"
            >
                <h3 className="text-3xl font-bold text-white mb-8 text-center">
                    Notre Écosystème Flutter
                </h3>

                <div className="grid md:grid-cols-3 gap-6">
                    {[
                        { name: "Riverpod", desc: "State management moderne", color: "from-blue-600 to-blue-800" },
                        { name: "Firebase", desc: "Backend as a Service", color: "from-yellow-600 to-orange-600" },
                        { name: "Dio", desc: "HTTP client puissant", color: "from-sky-600 to-cyan-600" },
                        { name: "GetX", desc: "Navigation & state", color: "from-purple-600 to-pink-600" },
                        { name: "Hive", desc: "Base de données locale", color: "from-amber-600 to-yellow-600" },
                        { name: "FlutterFlow", desc: "Prototypage rapide", color: "from-indigo-600 to-purple-600" }
                    ].map((tool, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.9 + index * 0.05 }}
                            whileHover={{ scale: 1.05 }}
                            className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-sky-500/30 transition-all duration-300"
                        >
                            <div className={`h-2 w-16 bg-gradient-to-r ${tool.color} rounded-full mb-4`} />
                            <h4 className="text-lg font-bold text-white mb-2">{tool.name}</h4>
                            <p className="text-gray-400 text-sm">{tool.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="text-center flex flex-col items-center justify-center bg-gradient-to-r from-blue-900/30 to-sky-900/30 backdrop-blur-xl rounded-3xl p-12 border border-sky-500/20"
            >
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Développons Votre App Flutter
                </h3>
                <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                    Une seule app, toutes les plateformes. Discutons de votre vision mobile
                </p>
                <motion.button
                    onClick={() => navigate("/Contact")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-[250px] sm:w-[320px] cursor-pointer px-6 py-4 sm:px-8 sm:py-5  text-center text-[14px] sm:text-lg font-medium flex items-center gap-1 bg-gradient-to-r from-sky-500 to-blue-500 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl hover:shadow-sky-500/50 transition-all duration-300 cursor-pointer"
                >
                    <span>Démarrer un projet Flutter</span>
                    <SmartphoneCharging className="w-5 h-5 md:w-6 md:h-6" />
                </motion.button>
            </motion.div>
        </div>
    );
}
